import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { ArrowLeft } from 'lucide-react';

/**
 * Navigation hierarchy configuration
 * Maps each route to its logical parent for consistent back navigation
 */
const NAVIGATION_HIERARCHY = {
  // Dashboard is the root - no parent
  '/': null,
  
  // ========== ESSENTIALS (Parent: Dashboard) ==========
  '/projects': '/',
  '/calendar': '/',
  '/va-library': '/',
  '/team-workspace': '/',
  '/workspace-files': '/',
  
  // ========== TOOLS (Parent: Dashboard) ==========
  '/document-creator': '/workspace-files', // Goes to Workspace Files, not Dashboard
  '/vendor-database': '/',
  '/market-research': '/',
  '/igce-generator': '/',
  '/compliance-checker': '/',
  '/gov-integrations': '/integrations',
  '/meeting-notes': '/',
  // '/rfi-wizard' removed - legacy feature deleted
  
  // ========== INSIGHTS (Parent: Dashboard) ==========
  '/analytics': '/',
  '/leadership-analytics': '/',
  '/portfolio': '/',
  '/acquisition-pipeline': '/',
  '/audit-dashboard': '/',
  '/risk-dashboard': '/',
  '/onboarding-analytics': '/admin',
  
  // ========== SYSTEM ==========
  '/settings': '/',
  '/integrations': '/settings',
  '/help': '/',
  '/admin': '/',
  '/super-admin': '/',
  '/audit-log': '/admin',
  
  // ========== PROJECT SUB-PAGES ==========
  '/contracts': '/projects',
  '/milestones': '/projects',
  '/risks': '/projects',
  
  // ========== EVALUATION HIERARCHY ==========
  '/evaluation': '/',
  '/evaluation-tools': '/evaluation',
  '/evaluation-workbench': '/evaluation',
  '/proposal-evaluator': '/evaluation',
  
  // ========== OPPORTUNITIES ==========
  '/opportunities': '/',
  '/solicitation-hub': '/',
  
  // ========== OTHER PAGES ==========
  '/vendor-portal': '/',
  '/collaboration-room': '/',
  '/stakeholder-portals': '/',
  '/contract-analyzer': '/va-library',
  '/clause-recommender': '/va-library',
  '/sdvosb-tracker': '/',
  '/quick-wins': '/',
  '/decision-wizard': '/',
  '/mobile-approval': '/',
  '/acquisition-timeline': '/',
  '/ai-learning': '/help',
  '/modules': '/',
  '/submissions': '/',
};

/**
 * Route labels for display
 * Maps routes to human-readable names for the breadcrumb
 */
const ROUTE_LABELS = {
  '/': 'Dashboard',
  '/projects': 'Projects',
  '/calendar': 'Calendar',
  '/va-library': 'VA Library',
  '/team-workspace': 'Team',
  '/workspace-files': 'Workspace Files',
  '/document-creator': 'Document Creator',
  '/vendor-database': 'Vendors',
  '/market-research': 'Market Research',
  '/igce-generator': 'IGCE Generator',
  '/compliance-checker': 'Compliance',
  '/gov-integrations': 'Gov Systems',
  '/meeting-notes': 'Meeting Notes',
  // '/rfi-wizard' removed - legacy feature deleted
  '/analytics': 'Analytics',
  '/leadership-analytics': 'Leadership View',
  '/portfolio': 'Portfolio',
  '/acquisition-pipeline': 'Pipeline',
  '/audit-dashboard': 'Audit Trail',
  '/risk-dashboard': 'Risk Monitor',
  '/onboarding-analytics': 'Onboarding Analytics',
  '/settings': 'Settings',
  '/integrations': 'Integrations',
  '/help': 'Help Center',
  '/admin': 'Admin Panel',
  '/super-admin': 'Super Admin',
  '/audit-log': 'Audit Log',
  '/contracts': 'Contracts',
  '/milestones': 'Milestones',
  '/risks': 'Risks',
  '/evaluation': 'Evaluation Room',
  '/evaluation-tools': 'Evaluation Tools',
  '/evaluation-workbench': 'Evaluation Workbench',
  '/proposal-evaluator': 'Proposal Evaluator',
  '/opportunities': 'Opportunities',
  '/solicitation-hub': 'Solicitation Hub',
  '/vendor-portal': 'Vendor Portal',
  '/collaboration-room': 'Collaboration Room',
  '/stakeholder-portals': 'Stakeholder Portals',
  '/contract-analyzer': 'Contract Analyzer',
  '/clause-recommender': 'Clause Recommender',
  '/sdvosb-tracker': 'SDVOSB Tracker',
  '/quick-wins': 'Quick Wins',
  '/decision-wizard': 'Decision Wizard',
  '/mobile-approval': 'Mobile Approvals',
  '/acquisition-timeline': 'Timeline',
  '/ai-learning': 'AI Learning',
  '/modules': 'Modules & Domains',
  '/submissions': 'Submissions',
};

/**
 * Get the parent route for the current path
 * @param {string} pathname - Current route path
 * @returns {string|null} Parent route or null if at root
 */
export const getParentRoute = (pathname) => {
  // Handle exact matches first
  if (NAVIGATION_HIERARCHY.hasOwnProperty(pathname)) {
    return NAVIGATION_HIERARCHY[pathname];
  }
  
  // Handle query params - strip them and try again
  const pathWithoutQuery = pathname.split('?')[0];
  if (NAVIGATION_HIERARCHY.hasOwnProperty(pathWithoutQuery)) {
    return NAVIGATION_HIERARCHY[pathWithoutQuery];
  }
  
  // Handle dynamic routes (e.g., /projects/123) - go to parent
  const pathParts = pathWithoutQuery.split('/').filter(Boolean);
  if (pathParts.length > 1) {
    const parentPath = '/' + pathParts.slice(0, -1).join('/');
    if (NAVIGATION_HIERARCHY.hasOwnProperty(parentPath)) {
      return parentPath;
    }
  }
  
  // Default to dashboard if route not found
  return '/';
};

/**
 * Get the label for a route
 * @param {string} pathname - Route path
 * @returns {string} Human-readable label
 */
export const getRouteLabel = (pathname) => {
  if (ROUTE_LABELS.hasOwnProperty(pathname)) {
    return ROUTE_LABELS[pathname];
  }
  
  // Handle query params
  const pathWithoutQuery = pathname.split('?')[0];
  if (ROUTE_LABELS.hasOwnProperty(pathWithoutQuery)) {
    return ROUTE_LABELS[pathWithoutQuery];
  }
  
  // Default label
  return 'Back';
};

/**
 * BackButton Component
 * Displays a breadcrumb-style back button that navigates to the logical parent route
 * Hidden on Dashboard (/) since it's the root
 * 
 * When a wizard registers a custom back handler via NavigationContext,
 * the button will use that handler instead of default navigation.
 * 
 * Also respects unsaved changes state and will trigger confirmation dialog
 * if there are unsaved changes on the current page.
 */
function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useTheme();
  const { 
    backHandler, 
    hasCustomBackHandler,
    hasUnsavedChanges,
    requestNavigation
  } = useNavigation();
  
  const currentPath = location.pathname;
  const parentRoute = getParentRoute(currentPath);
  const parentLabel = parentRoute ? getRouteLabel(parentRoute) : null;
  
  // Don't show on dashboard or if no parent (and no custom handler)
  if (currentPath === '/' || (!parentRoute && !hasCustomBackHandler)) {
    return null;
  }
  
  // Use custom handler label if provided, otherwise use parent label
  // Add indicator if there are unsaved changes
  let displayLabel = hasCustomBackHandler && backHandler?.label 
    ? backHandler.label 
    : parentLabel;
  
  const handleBack = () => {
    if (hasCustomBackHandler && backHandler?.onBack) {
      // Use the wizard's custom back handler (wizard handles its own unsaved state)
      backHandler.onBack();
    } else if (hasUnsavedChanges) {
      // Has unsaved changes - use navigation control to show dialog
      requestNavigation(() => navigate(parentRoute));
    } else {
      // Default: navigate to parent route
      navigate(parentRoute);
    }
  };
  
  return (
    <button
      onClick={handleBack}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
        darkMode
          ? 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
      }`}
      title={`Go back to ${displayLabel}${hasUnsavedChanges ? ' (unsaved changes)' : ''}`}
      data-testid="back-button"
      aria-label={`Navigate back to ${displayLabel}`}
    >
      <ArrowLeft 
        className={`w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5 ${
          darkMode ? 'text-gray-500 group-hover:text-gray-300' : 'text-gray-400 group-hover:text-gray-600'
        }`} 
      />
      <span className="hidden sm:inline">{displayLabel}</span>
      {hasUnsavedChanges && !hasCustomBackHandler && (
        <span className={`w-2 h-2 rounded-full ${darkMode ? 'bg-amber-400' : 'bg-amber-500'}`} title="Unsaved changes" />
      )}
    </button>
  );
}

export default BackButton;